/** Pure currency display shared by server adapters and client hooks; performs no FX conversion. */
export type FormatMoneyOptions = {
  /** Maximum decimal places (default 2). */
  precision?: number;
  /** Minimum decimal places (default 2). */
  minimumPrecision?: number;
  /** Render negatives as "(amount)" instead of "-amount". */
  parenthesisNegatives?: boolean;
  /** Viewer locale for separators and symbol position; the runtime's own when absent, as in the browser. */
  locale?: string;
};

const normalizeIntlCurrencyCode = (currencyCode: string): string => {
  const code = currencyCode.trim();
  return code ? code.toUpperCase() : "USD";
};

export const formatCurrencySymbol = (currencyCode: string): string => {
  const code = normalizeIntlCurrencyCode(currencyCode);
  const currencyPart = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: code,
    currencyDisplay: "narrowSymbol",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .formatToParts(0)
    .find(part => part.type === "currency");

  return currencyPart?.value ?? code;
};

/** Shared select/frozen label: `USD $` style code + narrow symbol. */
export const formatCurrencyOptionLabel = (currencyCode: string | null | undefined): string => {
  if (currencyCode == null || currencyCode.length === 0) {
    return "";
  }
  const code = normalizeIntlCurrencyCode(currencyCode);
  return `${code} ${formatCurrencySymbol(code)}`;
};

/**
 * Format a cent value as a currency string in the given currency.
 *
 * Strict parity with legacy `formatCurrency`, except the currency is a parameter
 * instead of hardcoded `"USD"`:
 * - `""` input → `""`.
 * - Strings are parsed with `parseFloat`; unparseable strings → 0 (legacy behavior).
 * - Zero always renders at 2 decimals (e.g. `$0.00`) regardless of `precision`,
 *   and never as `-$0.00` — matching legacy.
 *
 * @param value - Amount in **cents** (string | number | bigint).
 * @param currencyCode - ISO 4217 code (e.g. "USD", "CAD"). Empty/blank falls back to USD.
 * @param options - Precision and negative-formatting options.
 *
 * @remarks
 * bigint is converted via `Number()`. Values above `Number.MAX_SAFE_INTEGER`
 * (~$90 trillion in cents) lose precision silently — acceptable for display, as
 * such magnitudes indicate a data bug rather than a real amount.
 */
export const formatMoney = (value: string | number | bigint, currencyCode: string, options?: FormatMoneyOptions): string => {
  if (value === "") {
    return "";
  }

  const precision = options?.precision ?? 2;
  const minimumPrecision = options?.minimumPrecision ?? 2;
  const parenthesisNegatives = options?.parenthesisNegatives ?? false;

  // Guard before any Intl call: an empty/invalid code throws RangeError, and this
  // formatter backs the app-wide CurrencyDisplay choke point.
  const code = normalizeIntlCurrencyCode(currencyCode);

  // Clamp the minimum so it can never exceed the maximum (precision). Legacy
  // formatCurrency threw a RangeError when called with precision below the
  // default minimumPrecision of 2 (e.g. precision:0); this avoids that crash
  // without changing output for the normal precision >= minimumPrecision cases.
  const maximumFractionDigits = precision;
  const minimumFractionDigits = Math.min(minimumPrecision, precision);

  const n = typeof value === "string" ? parseFloat(value) || 0 : typeof value === "bigint" ? Number(value) : value;

  if (n === 0) {
    // Legacy returns a literal "$0.00" for zero (always 2 decimals, never -$0.00),
    // independent of the field precision. Preserve that, but in the org's currency.
    return new Intl.NumberFormat(options?.locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(0);
  }

  if (!parenthesisNegatives) {
    return new Intl.NumberFormat(options?.locale, {
      style: "currency",
      currency: code,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(n / 100);
  }

  const isNegative = n < 0;
  const formatted = new Intl.NumberFormat(options?.locale, {
    style: "currency",
    currency: code,
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(Math.abs(n) / 100);

  return isNegative ? `(${formatted})` : formatted;
};
